import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react'
import axios from 'axios'
import { useLocation } from 'react-router-dom'
import useUser from '../hooks/useUser'
import useProject from '../hooks/useProject'
import useHistory from '../hooks/useHistory'
import useTickers from '../hooks/useTickers'
import useSWR from "swr"
import { dayInMs } from '../utils/constants'

const GeneralContext = createContext()

export const GeneralProvider = ({ children }) => {
    const location = useLocation()
    const { pathname } = location
    const [AutoLoginStatus, setAutoLoginStatus] = useState(pathname.includes('/autologinfromAlthub')?true:false)

    const { isLoggedIn } = useUser()
    const { handleNewProject, checkIfExistProject } = useProject(isLoggedIn)
    const { tickers } = useTickers(isLoggedIn,AutoLoginStatus)
    const { openVisualiztionWithTicker } = useHistory()
    const {data: allDatasetsOptions} = useSWR(isLoggedIn && !AutoLoginStatus && '/api/getAllDatasets')
    // const { data: tickers } = useSWR(isLoggedIn && !AutoLoginStatus && `/api/tickers`, { dedupingInterval: dayInMs })

    const [projects, setProjects] = useState()
    const [projectTickerData, setprojectTickerData] = useState()
    const [alertOpen, setAlertOpen] = useState(false)
    const [alert, setAlert] = useState({type: 'error', message: ''})
    const [userAlerts, setUserAlerts] = useState([])
    const [WatchlistData, setWatchlistData] = useState([])

    useEffect(()=>{      
        setAutoLoginStatus(pathname.includes('/autologinfromAlthub')?true:false)
        return () => {}
    },[pathname,location])
    useEffect(() => {
        if(isLoggedIn &&!AutoLoginStatus){
            (async () => {
                try{
                    const resJson = await axios.get('/api/getProjects')
                    const res = await resJson.data
                    Array.isArray(res) && setProjects(res)
                }
                catch {
                    console.error('Failed to fetch project data')
                    setProjects()
                }
            })()
        }
        else {setProjects()}
        return () => {}
    },[isLoggedIn,AutoLoginStatus])
    
    useEffect(() => {
        if(isLoggedIn &&!AutoLoginStatus){
            (async () => {
                try{
                    const resJson = await axios.get('/api/getWatchlistData')
                    const res = await resJson.data
                    Array.isArray(res) && setWatchlistData(res)
                }
                catch {
                    console.error('Failed to fetch watchlist data')
                    setWatchlistData([])
                }
            })()
        }
        else {setWatchlistData([])}
        return () => {}
    },[isLoggedIn,AutoLoginStatus])

    useEffect(() => {
        setAlertOpen(!!alert?.message && !!alert?.type)
        return () => {}
    }, [alert])

    const handleCloseAlert = () => setAlertOpen(false)

    const setPopupAlert = (type, message) => setAlert({type, message})

    const rearangeProjects = (project) => {
        const filtered = projects.filter(projectFields => projectFields.uuid !== project.uuid)
        setProjects([{...project, updated_at: new Date().toLocaleDateString() }, ...filtered])
    }

    const handleOpenProject = async (ticker, dataset, column, rearagne = true) => {
        if (ticker){            
            const resJson = await axios.get(`/api/getInfo?ticker=${ticker}`)
            const res = await resJson.data
            let project = await handleNewProject(projects, ticker, dataset, column)
            if (res){
                project['currency_code'] = res['currency_code']
                project['currency_symbol'] = res['currency_symbol']
            }
            rearagne && rearangeProjects(project)
            return project
        }
    }

    const handleCreateProjectAndOpen = async (ticker, feed = false, rearagne = true) => {
        const project = await handleOpenProject(ticker, '', '', false)
        openVisualiztionWithTicker(project.uuid, ticker, feed)
        rearagne && rearangeProjects(project)
        return project
    }

    const submitOnBoardingForm = (onBoardingForm) => {
        // axios('/api/updateNewUserData', {
        //     method: 'POST',
        //     headers: { 'Content-Type': 'application/json' },
        //     body: JSON.stringify(onBoardingForm)
        //   })
        axios({
            method: 'POST',
            url: '/api/updateNewUserData',
            data: onBoardingForm
        })
          .then(res => res.json())
          .then(res => res.status === 200 && setProjects(res.proejcts)) 
    }

    const watchlistProjects = useMemo(() => projects?.filter(project => project.portfolio?.added), [projects])

    const getWatchlistStatus = (projectId) => {
        const added = projects ? projects.find(project => project.uuid === projectId)?.portfolio?.added : null
        return {
            added,
            action: added? 'Remove from watchlist': 'Add to watchlist'
        }
    }

    const toggleWatchlist = async (uuid) => {
        const project = projects.find(project => project.uuid === uuid)
        const added = !project?.portfolio?.added
        const updatedProject = {...project, portfolio: {added}}
        const endpoint = added
            ? '/api/addProjectToWatchlist'
            : '/api/removeProjectFromWatchlist'
        // await fetch(endpoint, {
        //         method: 'PATCH',
        //         headers: {'Content-Type': 'application/json'},
        //         body: JSON.stringify({ projectId: project.uuid })
        // })
        await axios({
            method: 'PATCH',
            url: endpoint,
            data: { projectId: project.uuid }
        })
        .then(()=>rearangeProjects(updatedProject))
        
    }

    const checkIfProjectExist = useCallback((ticker) => checkIfExistProject(ticker, projects),[projects, checkIfExistProject])

    //alerts

    useEffect(() => {
        isLoggedIn &&!AutoLoginStatus && axios.get(`/api/getUserAlerts`).then(res => res.data)
        .then(res => setUserAlerts(res))
        return () => {}
    }, [isLoggedIn,AutoLoginStatus])

    const userTriggerdAlerts = useMemo(() => {
        if ( !userAlerts ) { return [] }
        const twoMonths = (new Date()).setMonth(new Date().getMonth() -2)
        const triggerdAlerts = userAlerts
            .filter(({triggered_at}) => triggered_at && new Date(triggered_at) > twoMonths)
            .map(alert => {
                const d = new Date(alert.triggered_at)
                return {...alert, triggered_at: `${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()}`}
            })
        const alertsDict = {}
        triggerdAlerts?.forEach(alert => {
            if (alertsDict.hasOwnProperty(alert.ticker)) alertsDict[alert.ticker].push(alert)
            else alertsDict[alert.ticker] = [alert]
        })
        return alertsDict
    }, [userAlerts])

    return (
        
        <GeneralContext.Provider value={{
            setAutoLoginStatus,
            AutoLoginStatus,
            alertOpen,
            alert,
            setPopupAlert,
            handleCloseAlert,
            projects,
            handleOpenProject,
            watchlistProjects,
            submitOnBoardingForm,
            handleCreateProjectAndOpen,
            projectMethods: {
                toggleWatchlist,
                getWatchlistStatus,
            },
            openVisualiztionWithTicker,
            checkIfProjectExist,
            tickers: projects && tickers,
            userAlerts,
            setUserAlerts,
            userTriggerdAlerts,
            allDatasetsOptions,
            setprojectTickerData,
            projectTickerData,
            WatchlistData,
            setWatchlistData
        }}>
            { children}
        </GeneralContext.Provider>
    )
}

export function useGeneral() {
    return useContext(GeneralContext)
}